import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import {
  getAreaBoundary,
  getAreaByParent,
  getAreaList,
  getAreaWithPropertyList,
  getAreaWithScrapedList,
  getStateList,
  getStateWithBoundaryList,
  getCityWithBoundaryList,
  listCityWithSTRByState,
  getAllAreaList,
} from "src/api/area";

type IUseStateListRequest = () => UseQueryResult<AxiosResponse<API.Area.List.Response>, AxiosError<API.Area.List.ErrorResponse>>;

export const useStateListRequest: IUseStateListRequest = () => useQuery(["useStateListRequest"], getStateList);

type IUseStateWithBoundaryListRequest = (
  enabled?: boolean,
) => UseQueryResult<AxiosResponse<API.Area.ListWithBoundary.Response>, AxiosError<API.Area.ListWithBoundary.ErrorResponse>>;

export const useStateWithBoundaryListRequest: IUseStateWithBoundaryListRequest = (enabled = true) =>
  useQuery(["useStateWithBoundaryListRequest"], getStateWithBoundaryList, {
    enabled,
  });

type IUseCityWithBoundaryListRequest = (
  params?: API.Area.ListWithBoundary.Request,
  enabled?: boolean,
) => UseQueryResult<AxiosResponse<API.Area.ListWithBoundary.Response>, AxiosError<API.Area.ListWithBoundary.ErrorResponse>>;

export const useCityWithBoundaryListRequest: IUseCityWithBoundaryListRequest = (params, enabled) =>
  useQuery(["useCityWithBoundaryListRequest", params], async () => await getCityWithBoundaryList(params), {
    enabled,
    keepPreviousData: true,
  });

type IUseAreaChildListRequest = (
  parentId: API.Area.AreaData["id"],
) => UseQueryResult<AxiosResponse<API.Area.List.Response>, AxiosError<API.Area.List.ErrorResponse>>;

export const useAreaChildListRequest: IUseAreaChildListRequest = (parentId) =>
  useQuery(["useAreaRequest", parentId], async () => await getAreaByParent(parentId), {
    enabled: false,
  });

type IUseAreaWithPropertiesListRequest = (
  params: API.Area.List.Request,
) => UseQueryResult<AxiosResponse<API.Area.List.Response>, AxiosError<API.Area.List.ErrorResponse>>;

export const useAreaWithPropertiesListRequest: IUseAreaWithPropertiesListRequest = (params) =>
  useQuery(["useAreaWithPropertiesListRequest", params], async () => await getAreaWithPropertyList(params), {
    enabled: false,
  });

type IUseAreaWithScrapedListRequest = (
  params: API.Area.List.Request,
) => UseQueryResult<AxiosResponse<API.Area.List.Response>, AxiosError<API.Area.List.ErrorResponse>>;

export const useAreaWithScrapedListRequest: IUseAreaWithScrapedListRequest = (params) =>
  useQuery(["useAreaWithScrapedListRequest", params], async () => await getAreaWithScrapedList(params), {
    enabled: false,
  });

type IUseAreaListRequest = (
  params: API.Area.List.Request,
  enabled?: boolean,
) => UseQueryResult<AxiosResponse<API.Area.List.Response>, AxiosError<API.Area.List.ErrorResponse>>;

export const useAreaListRequest: IUseAreaListRequest = (params, enabled = false) =>
  useQuery(["useAreaRequest", params], async () => await getAreaList(params), {
    enabled,
    keepPreviousData: true,
  });

type IUseAreaListCityRequest = (
  params: API.Area.ListCity.Request,
  enabled?: boolean,
) => UseQueryResult<AxiosResponse<API.Area.ListCity.Response>, AxiosError<API.Area.ListCity.ErrorResponse>>;

export const useAreaListCityRequest: IUseAreaListCityRequest = (params, enabled = false) =>
  useQuery(["useAreaListCityRequest", params], async () => await getAllAreaList(params), {
    enabled,
    keepPreviousData: true,
  });

type IUseCityWithSTRListRequest = (
  parentId: API.Area.AreaData["id"],
) => UseQueryResult<AxiosResponse<API.Area.List.Response>, AxiosError<API.Area.List.ErrorResponse>>;

export const useCityWithSTRListRequest: IUseCityWithSTRListRequest = (stateId) =>
  useQuery(["useCityWithSTRListRequest", stateId.toString()], async () => await listCityWithSTRByState(stateId), {
    enabled: false,
  });

type IUseAreaBoundaryRequest = (
  parentId: API.Area.AreaData["id"],
) => UseQueryResult<AxiosResponse<API.Area.AreaBoundary.Response>, AxiosError<API.Area.AreaBoundary.ErrorResponse>>;

export const useAreaBoundaryRequest: IUseAreaBoundaryRequest = (areaId) =>
  useQuery(["useAreaBoundaryRequest", areaId.toString()], async () => await getAreaBoundary(areaId), {
    enabled: false,
    keepPreviousData: true,
  });

type IUseAreaStateCityToIDRequest = (
  state: string,
  city: string,
) => UseQueryResult<AxiosResponse<API.Area.AnalyzerData>, AxiosError<API.Area.AreaData>>;
